import dayjs, { Dayjs, isDayjs } from 'dayjs';
import Duration from 'dayjs/plugin/duration';
import IsSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';
import isNil from 'lodash/isNil';
import { clientConfig } from 'pos-client/clientConfig.ts';
import {
  DurationUnitEnum,
  type DurationUnitType,
} from 'pos-shared/models/schedulingSchema.ts';

dayjs.extend(Duration);
dayjs.extend(IsSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localeData);

type TimeRange = {
  start: string;
  end: string;
  original: string;
};

export const isDayBeforeToday = (current: dayjs.Dayjs): boolean =>
  current.diff(dayjs().subtract(1, 'day'), 'date') < 0;

export const isDayTodayOrBefore = (current: dayjs.Dayjs): boolean =>
  current.diff(dayjs(), 'date') <= 0;

export const toDayjsOrUndefined = (
  v?: Date | string | null
): dayjs.Dayjs | undefined => (v ? dayjs(v) : undefined);

export const isTodayOrFuture = (input: dayjs.Dayjs): boolean =>
  dayjs(input).isAfter(dayjs().subtract(1, 'day').endOf('day'));

export const isBeforeToday = (input: dayjs.Dayjs): boolean =>
  dayjs(input).isBefore(dayjs().startOf('day'));

export const isWithinNextXDays = (input: dayjs.Dayjs, xDays: number): boolean =>
  dayjs(input).isBefore(
    dayjs()
      .add(xDays + 1, 'day')
      .startOf('day')
  );

export function isToday(input: dayjs.Dayjs | Date | string) {
  return dayjs(input).isSame(dayjs(), 'day');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formDayJsValue = (date: any) => {
  if (isNil(date)) {
    return null;
  }

  return isDayjs(date) && dayjs(date).isValid() ? dayjs(date).utc(true) : null;
};

export const today = dayjs();

export const formatTimeAmPm = (timeString: string) => {
  const formatDate = new Date(`${new Date().toDateString()} ${timeString}`);
  return dayjs(formatDate).format('h A');
};

export const formatDurationUnit = (value: number, unit: DurationUnitType) =>
  t(`common.time.unitsWithCount.${unit}`, { count: value });

export const calculateDurationStatus = (
  durationStart: dayjs.Dayjs,
  durationEnd: dayjs.Dayjs
) =>
  durationStart.isSame(durationEnd)
    ? 'onTime'
    : durationStart.isBefore(durationEnd)
      ? 'ahead'
      : 'behind';

export const humaniseDuration = (
  durationStart: dayjs.Dayjs,
  durationEnd: dayjs.Dayjs,
  displayRelativePosition?: boolean, // Will show the relative position of the duration (e.g. ahead, on time, behind)
  excludeUnits?: DurationUnitType[]
) => {
  const duration = dayjs.duration(durationEnd.diff(durationStart));

  // Will omit a duration unit when specified in 'excludeUnits' despite having a value
  const addDurationUnit = (unit: DurationUnitType) => {
    if (!excludeUnits?.includes(unit)) {
      const value = Math.abs(duration.get(unit));
      return value > 0 ? formatDurationUnit(value, unit) : '';
    }
    return '';
  };
  const relativeTimePosition = calculateDurationStatus(
    durationStart,
    durationEnd
  );
  // Duration string will always return to the highest unit (e.g. if duration seconds are 60, it'll show 1 minute and not display 60 seconds)
  const durationString = [
    addDurationUnit('year'),
    addDurationUnit('month'),
    addDurationUnit('day'),
    addDurationUnit('hour'),
    addDurationUnit('minute'),
    addDurationUnit('second'),
    addDurationUnit('millisecond'),
  ]
    .filter(Boolean)
    .join(' ');

  return relativeTimePosition === 'onTime' && displayRelativePosition
    ? t('common.time.relativePosition.onTime')
    : `${durationString} ${
        displayRelativePosition
          ? t(`common.time.relativePosition.${relativeTimePosition}`)
          : ''
      }`;
};

const shorthandUnits = {
  year: 'y',
  month: 'mo',
  day: 'd',
  hour: 'h',
  minute: 'm',
  second: 's',
  millisecond: 'ms',
};

// Returns the total duration provided in shorthand format
export const formatDurationShorthand = (
  total: number,
  unit: DurationUnitType,
  excludeUnits: DurationUnitType[] = []
) => {
  const duration = dayjs.duration(total, unit);
  const units = DurationUnitEnum.options;

  return units
    .filter((u) => !excludeUnits.includes(u) && duration.get(u) > 0)
    .map((u) => `${duration.get(u)}${shorthandUnits[u]}`)
    .join(' ');
};

export const getLaterDate = (dateOne: Date, dateTwo: Date) =>
  dayjs(dateOne).isSameOrAfter(dateTwo, 'second') ? dateOne : dateTwo;

// Used for sorting ViewAvailability Table
const parseTimeRange = (range: string): TimeRange => {
  const [start, end] = range.split(' - ');
  const startTime = dayjs(start, 'h:mm A').format('HH:mm');
  const endTime = dayjs(end, 'h:mm A').format('HH:mm');
  return { start: startTime, end: endTime, original: range };
};

// Sort Array of time ranges
export const sortTimeRanges = (timeRanges: string[]): string[] => {
  return timeRanges
    .map((range) => parseTimeRange(range)) // Convert each range to a TimeRange object with sortable times
    .sort((a, b) => {
      // First, compare by start times
      if (a.start < b.start) return -1;
      if (a.start > b.start) return 1;
      // If start times are equal, compare by end times
      if (a.end < b.end) return -1;
      if (a.end > b.end) return 1;
      return 0;
    })
    .map((obj) => obj.original); // Extract the original range strings in sorted order
};

export const formatUTCDateTime = (date: Date | string) => {
  const dateFormat =
    clientConfig.locale === 'en-GB'
      ? 'DD/MM/YYYY, H:mm A'
      : 'MM/DD/YYYY, H:mm A';

  return dayjs.utc(date).format(dateFormat);
};

export const setUtcOffset = (
  date: Date | string | undefined,
  tz: string | undefined
) => {
  if (date == null) return null;
  return tz == null
    ? dayjs.utc(date)
    : dayjs.utc(date).utcOffset(tz as string, true);
};

export const nextDate = (dates: (Date | string | Dayjs)[]): Dayjs | undefined =>
  dates
    .map((date) => (isDayjs(date) ? date : dayjs(date)))
    .filter((date) => date.isAfter(dayjs()))
    .sort((a, b) => a.valueOf() - b.valueOf())[0] ?? undefined;

export const getGreeting = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hour = dayjs().tz(tz).hour();

    const key =
      hour >= 5 && hour < 12
        ? t('common.time.greetings.goodMorning')
        : hour >= 12 && hour < 18
          ? t('common.time.greetings.goodAfternoon')
          : t('common.time.greetings.goodEvening');

    return key;
  } catch (error) {
    return t('common.time.greetings.hello');
  }
};

/**
 * Return abbreviated weekday names for a locale (e.g. Mon, Tue, ...).
 */
export function getShortWeekdays(): string[] {
  return dayjs().localeData().weekdaysShort();
}
